import { useEffect, useMemo, useState } from "react";
import { PackageOpen, Plus, Trash2, Users, DollarSign, TrendingUp, ChevronRight, X, MessageCircle, Check } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

function fmt(n, cur = "XOF") {
  const v = Number(n || 0);
  return `${Math.round(v).toLocaleString("fr-FR")} ${cur}`;
}
function formatDate(iso) {
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
}

export default function GroupagePanel() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [err, setErr] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState({ name: "", currency: "XOF", notes: "" });

  const load = async () => {
    try { const { data } = await api.get("/admin/groupages"); setItems(data || []); }
    catch (e) { setErr(formatApiError(e)); }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/groupages", newForm);
      setNewForm({ name: "", currency: "XOF", notes: "" });
      setShowNew(false);
      await load();
      setSelected(data);
    } catch (e) { setErr(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer ce groupage et toutes ses contributions ?")) return;
    await api.delete(`/admin/groupages/${id}`);
    if (selected?.id === id) setSelected(null);
    load();
  };

  const totals = useMemo(() => items.reduce((a, g) => ({
    count: a.count + 1,
    received: a.received + (g.total_received || 0),
    spent: a.spent + (g.total_spent || 0),
    clients: a.clients + (g.client_count || 0),
  }), { count: 0, received: 0, spent: 0, clients: 0 }), [items]);

  return (
    <div className="space-y-6" data-testid="admin-groupage">
      {err && <p className="text-sm text-red-500">{err}</p>}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={PackageOpen} label="Groupages actifs" value={totals.count} />
        <StatCard icon={Users} label="Clients participants" value={totals.clients} />
        <StatCard icon={DollarSign} label="Total encaissé" value={fmt(totals.received, "XOF")} small />
        <StatCard icon={TrendingUp} label="Total dépensé" value={fmt(totals.spent, "XOF")} small />
      </div>

      <div className="flex items-center justify-between">
        <h3 className="font-display text-xl font-bold flex items-center gap-2"><PackageOpen size={18}/> Groupages en cours</h3>
        <button onClick={() => setShowNew(!showNew)} className="btn-primary !py-2 !px-3 text-xs" data-testid="new-groupage-btn">
          <Plus size={14}/> Nouveau groupage
        </button>
      </div>

      {showNew && (
        <form onSubmit={create} className="card-flat p-6 space-y-3" data-testid="new-groupage-form">
          <input required placeholder="Nom du groupage (ex: Container Yiwu Mars 2026)" value={newForm.name} onChange={(e)=>setNewForm({...newForm, name:e.target.value})} className="input-field" data-testid="groupage-name" />
          <div className="flex gap-2">
            <select value={newForm.currency} onChange={(e)=>setNewForm({...newForm, currency:e.target.value})} className="input-field w-32">
              <option value="XOF">FCFA</option><option value="EUR">EUR</option><option value="USD">USD</option><option value="CNY">CNY</option>
            </select>
            <textarea placeholder="Notes internes (destination, deadline...)" value={newForm.notes} onChange={(e)=>setNewForm({...newForm, notes:e.target.value})} className="input-field flex-1 min-h-[50px]" />
          </div>
          <button type="submit" className="btn-primary w-full justify-center" data-testid="groupage-create-submit"><Plus size={14}/> Créer le groupage</button>
        </form>
      )}

      <div className="space-y-3">
        {items.length === 0 ? (
          <div className="card-flat p-10 text-center text-sm text-[var(--ink-soft)]">
            Aucun groupage en cours. Créez-en un pour démarrer le suivi des contributions clients.
          </div>
        ) : items.map((g) => (
          <div key={g.id} className={`card-flat overflow-hidden ${selected?.id === g.id ? "ring-2 ring-[var(--accent)]" : ""}`} data-testid={`groupage-${g.id}`}>
            <button onClick={() => setSelected(selected?.id === g.id ? null : g)} className="w-full text-left p-4 flex items-center gap-3 hover:bg-[var(--bg-soft)]">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs text-[var(--ink-soft)]">{g.reference}</span>
                  <p className="font-bold">{g.name}</p>
                  <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${g.status === "closed" ? "bg-black/60 text-white" : g.status === "shipped" ? "bg-green-500 text-white" : "bg-[var(--accent)] text-white"}`}>
                    {g.status}
                  </span>
                </div>
                <div className="flex flex-wrap gap-3 text-xs text-[var(--ink-soft)] mt-1">
                  <span>👥 {g.client_count} client{g.client_count > 1 ? "s" : ""}</span>
                  <span className="text-green-600 font-bold">💰 Reçu : {fmt(g.total_received, g.currency)}</span>
                  <span className="text-orange-600 font-bold">🛍️ Dépensé : {fmt(g.total_spent, g.currency)}</span>
                  <span className={g.balance >= 0 ? "text-blue-600 font-bold" : "text-red-600 font-bold"}>⚖️ Solde : {fmt(g.balance, g.currency)}</span>
                </div>
              </div>
              <ChevronRight size={18} className={`text-[var(--ink-soft)] transition-transform ${selected?.id === g.id ? "rotate-90" : ""}`} />
            </button>
            {selected?.id === g.id && (
              <GroupageDetail groupage={g} onChanged={load} onDelete={() => del(g.id)} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, small }) {
  return (
    <div className="card-flat p-5">
      <Icon className="text-[var(--accent)]" size={18} />
      <p className="text-xs uppercase tracking-widest text-[var(--ink-soft)] mt-3">{label}</p>
      <p className={`font-display ${small ? "text-lg" : "text-2xl"} font-black mt-1`}>{value}</p>
    </div>
  );
}

function GroupageDetail({ groupage: g, onChanged, onDelete }) {
  const [form, setForm] = useState({ client_name: "", client_phone: "", amount_received: "", notes: "", products: [] });
  const [prodInput, setProdInput] = useState({ name: "", qty: "1", unit_price: "", note: "" });

  const addProduct = () => {
    if (!prodInput.name.trim()) return;
    setForm((f) => ({ ...f, products: [...(f.products || []), {
      name: prodInput.name.trim(),
      qty: prodInput.qty ? parseFloat(prodInput.qty) : 1,
      unit_price: prodInput.unit_price ? parseFloat(prodInput.unit_price) : null,
      note: prodInput.note || null,
      purchased: false,
    }] }));
    setProdInput({ name: "", qty: "1", unit_price: "", note: "" });
  };

  const removeFormProduct = (i) => setForm((f) => ({ ...f, products: f.products.filter((_, idx) => idx !== i) }));

  const submitContribution = async (e) => {
    e.preventDefault();
    const payload = {
      client_name: form.client_name,
      client_phone: form.client_phone || null,
      amount_received: parseFloat(form.amount_received),
      currency: g.currency,
      products: form.products,
      notes: form.notes || null,
    };
    await api.post(`/admin/groupages/${g.id}/contributions`, payload);
    setForm({ client_name: "", client_phone: "", amount_received: "", notes: "", products: [] });
    onChanged();
  };

  const removeContrib = async (cid) => {
    if (!window.confirm("Retirer cette contribution ?")) return;
    await api.delete(`/admin/groupages/${g.id}/contributions/${cid}`);
    onChanged();
  };

  const togglePurchased = async (c, pIdx) => {
    const updated = { ...c, products: c.products.map((p, i) => i === pIdx ? { ...p, purchased: !p.purchased } : p) };
    await api.put(`/admin/groupages/${g.id}/contributions/${c.id}`, updated);
    onChanged();
  };

  return (
    <div className="border-t border-black/5 p-5 space-y-6 bg-[var(--bg-soft)]/40" data-testid={`groupage-detail-${g.id}`}>
      {/* Contributions table */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="kicker">Contributions ({g.contributions?.length || 0})</p>
          <button onClick={onDelete} className="text-red-500 hover:bg-red-50 rounded p-2" title="Supprimer le groupage"><Trash2 size={14}/></button>
        </div>
        {(g.contributions || []).length === 0 ? (
          <p className="text-sm text-[var(--ink-soft)] italic">Aucune contribution. Ajoutez le premier client ci-dessous.</p>
        ) : (
          <div className="space-y-2">
            {g.contributions.map((c) => {
              const spent = (c.products || []).reduce((a, p) => a + (Number(p.unit_price || 0) * Number(p.qty || 0)), 0);
              const remaining = Number(c.amount_received || 0) - spent;
              return (
                <div key={c.id} className="card-flat p-4" data-testid={`contribution-${c.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <p className="font-bold">{c.client_name}</p>
                      {c.client_phone && <p className="text-xs text-[var(--ink-soft)]">📱 {c.client_phone}</p>}
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-[var(--ink-soft)] uppercase tracking-widest">Reçu</p>
                      <p className="font-display text-xl font-black text-green-600">{fmt(c.amount_received, c.currency)}</p>
                      <p className="text-[10px] text-[var(--ink-soft)]">Dépensé : {fmt(spent, c.currency)} · Reste : <span className={remaining >= 0 ? "text-blue-600 font-bold" : "text-red-600 font-bold"}>{fmt(remaining, c.currency)}</span></p>
                    </div>
                  </div>
                  {(c.products || []).length > 0 && (
                    <table className="w-full mt-3 text-sm">
                      <thead>
                        <tr className="text-xs uppercase tracking-widest text-[var(--ink-soft)] text-left">
                          <th className="pb-2">Produit</th>
                          <th className="pb-2">Qté</th>
                          <th className="pb-2">P.U.</th>
                          <th className="pb-2">Total</th>
                          <th className="pb-2 text-right">Acheté</th>
                        </tr>
                      </thead>
                      <tbody>
                        {c.products.map((p, i) => (
                          <tr key={i} className="border-t border-black/5">
                            <td className="py-1.5">{p.name} {p.note && <span className="text-[var(--ink-soft)] text-xs italic">— {p.note}</span>}</td>
                            <td className="py-1.5">{p.qty || "—"}</td>
                            <td className="py-1.5">{p.unit_price ? fmt(p.unit_price, c.currency) : "—"}</td>
                            <td className="py-1.5 font-bold">{p.unit_price ? fmt(Number(p.unit_price) * Number(p.qty || 1), c.currency) : "—"}</td>
                            <td className="py-1.5 text-right">
                              <button onClick={() => togglePurchased(c, i)} className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full ${p.purchased ? "bg-green-500 text-white" : "bg-black/10 text-[var(--ink-soft)]"}`}>
                                {p.purchased ? <Check size={11}/> : null} {p.purchased ? "Fait" : "À faire"}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  {c.notes && <p className="mt-2 text-xs italic text-[var(--ink-soft)]">« {c.notes} »</p>}
                  <div className="flex justify-between items-center mt-3 pt-3 border-t border-black/5">
                    <span className="text-[10px] text-[var(--ink-soft)]">Ajouté le {formatDate(c.at)}</span>
                    <div className="flex gap-1">
                      {c.client_phone && (
                        <a href={`https://wa.me/${String(c.client_phone).replace(/[^\d]/g,"")}`} target="_blank" rel="noopener noreferrer" className="text-[#25D366] hover:bg-green-50 rounded p-1.5" title="WhatsApp"><MessageCircle size={14}/></a>
                      )}
                      <button onClick={() => removeContrib(c.id)} className="text-red-500 hover:bg-red-50 rounded p-1.5" title="Supprimer"><Trash2 size={14}/></button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add contribution form */}
      <form onSubmit={submitContribution} className="card-flat p-5 space-y-3 border-2 border-dashed border-[var(--accent)]/30" data-testid="add-contribution-form">
        <p className="kicker !text-[var(--accent)]">Ajouter un client au groupage</p>
        <div className="grid md:grid-cols-3 gap-3">
          <input required placeholder="Nom du client" value={form.client_name} onChange={(e)=>setForm({...form, client_name:e.target.value})} className="input-field" data-testid="contrib-name" />
          <input placeholder="Téléphone WhatsApp" value={form.client_phone} onChange={(e)=>setForm({...form, client_phone:e.target.value})} className="input-field" />
          <input required type="number" step="100" placeholder={`Montant reçu (${g.currency})`} value={form.amount_received} onChange={(e)=>setForm({...form, amount_received:e.target.value})} className="input-field" data-testid="contrib-amount" />
        </div>

        {/* Product picker */}
        <div className="pt-3 border-t border-black/5 space-y-2">
          <p className="text-xs font-bold uppercase tracking-widest text-[var(--ink-soft)]">Produits à acheter pour ce client</p>
          {form.products.length > 0 && (
            <ul className="space-y-1">
              {form.products.map((p, i) => (
                <li key={i} className="flex items-center gap-2 text-sm bg-[var(--bg-soft)] rounded px-3 py-1.5">
                  <span className="flex-1"><strong>{p.name}</strong> · qté {p.qty} {p.unit_price ? `· ${fmt(p.unit_price, g.currency)}/u` : ""} {p.note ? `· ${p.note}` : ""}</span>
                  <button type="button" onClick={() => removeFormProduct(i)} className="text-red-500 hover:bg-red-50 rounded p-1"><X size={12}/></button>
                </li>
              ))}
            </ul>
          )}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <input placeholder="Nom produit" value={prodInput.name} onChange={(e)=>setProdInput({...prodInput, name:e.target.value})} className="input-field col-span-2" />
            <input type="number" step="1" placeholder="Qté" value={prodInput.qty} onChange={(e)=>setProdInput({...prodInput, qty:e.target.value})} className="input-field" />
            <input type="number" step="100" placeholder="P.U." value={prodInput.unit_price} onChange={(e)=>setProdInput({...prodInput, unit_price:e.target.value})} className="input-field" />
            <button type="button" onClick={addProduct} className="btn-outline !py-2 !px-3 text-xs"><Plus size={12}/> Ajouter</button>
          </div>
        </div>

        <textarea placeholder="Notes (préférences, remarques...)" value={form.notes} onChange={(e)=>setForm({...form, notes:e.target.value})} className="input-field min-h-[50px]" />
        <button type="submit" className="btn-primary w-full justify-center" data-testid="contrib-submit"><Plus size={14}/> Enregistrer la contribution</button>
      </form>
    </div>
  );
}
